import type { BaseWidgetData } from '~/@types/cms';

export interface IWidgetProps<T> {
    data: T;
    componentIndex?: number;
}

export interface UrlLink {
    url?: string;
    label?: string | null;
    target?: string;
}

export const useWidget = <T extends BaseWidgetData>(props: IWidgetProps<T>) => {
    const getValueSequence = <TM extends BaseWidgetData>(value: WidgetList<TM>) => {
        if (!(value && value.sequence && value.components)) return [];
        return value.sequence.map((id) => value.components.find((c) => c.id === id)).filter(isDefined);
    };

    return {
        data: props.data,
        getValueSequence,
    };
};

export const useInjectWidgetInfo = (campaignName?: string) => {
    const { name, index } = inject(WIDGET_INFO_INJECTION_KEY, { name: '', index: -1 });
    const slideIndex = inject(SWIPER_SLIDE_INDEX_INJECTION_KEY, -1);

    const id = arrayToId([campaignName, name, index >= 0 ? `${index}` : undefined, slideIndex >= 0 ? `${slideIndex}` : undefined]);
    return { name, index, id };
};

export const useProvideWidgetInfo = <T extends BaseWidgetData>(props: IWidgetProps<T>) => {
    // get existing provided parent widget info
    const { id } = useInjectWidgetInfo();
    const componentName = getCurrentInstance()?.type?.__name ?? 'unknown';
    // uses index > 100 if no index is provided
    const componentIndex =
        props.componentIndex ||
        useId()
            .split('')
            .reduce((sum, char) => sum + char.charCodeAt(0), 0);
    provide(WIDGET_INFO_INJECTION_KEY, {
        name: arrayToId([id, componentName]),
        index: componentIndex,
    });
};
